import { useState, useCallback, useEffect } from 'react';
import { log } from '../utils/logger';

/**
 * Custom hook for managing reservation success state across components
 * @returns {Object} Reservation success state and handlers
 */
export const useReservationSuccess = () => {
  const [reservationSuccess, setReservationSuccess] = useState(null);

  // Check for reservation success data in sessionStorage on mount
  useEffect(() => {
    const storedSuccess = sessionStorage.getItem('reservationSuccess');
    if (storedSuccess) {
      try {
        const successData = JSON.parse(storedSuccess);
        log.info('🎉 Found stored reservation success data:', successData);
        setReservationSuccess(successData);
        // Keep it in sessionStorage until manually cleared
      } catch (err) {
      // Error caught and ignored
        log.error('Error parsing stored reservation success:', err);
        sessionStorage.removeItem('reservationSuccess');
      }
    }
  }, []);

  // Handle new reservation success
  const handleReservationSuccess = useCallback((successData) => {
    log.info('🎉 Setting reservation success:', successData);
    
    // Ensure timezone is included in the success data
    const enhancedSuccessData = {
      ...successData,
      timestamp: new Date().toISOString() // Add timestamp for reference
    };
    
    setReservationSuccess(enhancedSuccessData);
    
    // Save to sessionStorage for persistence across tab switches
    sessionStorage.setItem('reservationSuccess', JSON.stringify(enhancedSuccessData));
    log.info('🎉 Saved reservation success to sessionStorage');
  }, []);

  // Clear reservation success
  const clearReservationSuccess = useCallback(() => {
    log.info('🎉 Clearing reservation success');
    setReservationSuccess(null);
    sessionStorage.removeItem('reservationSuccess');
  }, []);

  // Handle "Create Another Reservation" action
  const handleCreateAnother = useCallback((onClearCallback) => {
    log.info('🎉 Creating another reservation');
    clearReservationSuccess();
    
    // Call the callback to reset form states if provided
    if (onClearCallback && typeof onClearCallback === 'function') {
      onClearCallback();
    }
  }, [clearReservationSuccess]);

  // Handle "View Reservations" action  
  const handleViewReservations = useCallback((setActiveTabCallback) => {
    log.info('🎉 Switching to view reservations');
    clearReservationSuccess();
    
    // Call the callback to switch tabs if provided
    if (setActiveTabCallback && typeof setActiveTabCallback === 'function') {
      setActiveTabCallback('reservations');
    }
  }, [clearReservationSuccess]);

  // Check if there's currently a reservation success state
  const hasReservationSuccess = Boolean(reservationSuccess);

  return {
    // State
    reservationSuccess,
    hasReservationSuccess,
    
    // Actions
    handleReservationSuccess,
    clearReservationSuccess,
    handleCreateAnother,
    handleViewReservations,
    
    // Direct setter for special cases
    setReservationSuccess
  };
};